/* eslint-disable max-lines-per-function */
import { Icon20ArrowUturnLeftOutline, Icon20ChevronUpOutline, Icon20DeleteOutline } from '@vkontakte/icons';
import { Button, unstable_Popover as Popover } from '@vkontakte/vkui';
import { ReactComponent as SafeBoxIcon } from 'img/icons/safe_box_outline_20.svg';
import React, { type FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { overquotaSafeVariantC } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { sendDwhButton, sendDwhTooltip } from 'reactApp/modules/dwh/dwh.helpers';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { removeFromSafeAction, restoreFromSafeRequest } from 'reactApp/modules/trashbin/trashbin.module';
import { isSuccessfullyStoredToSafe } from 'reactApp/modules/trashbin/trashbin.selectors';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { DropdownOutlineIcon } from 'reactApp/ui/VKUIIcons';

import styles from './SafeDropdownButton.css';

const TOOLTIP_ID = 'tooltip-restore-from-safe';
const name = 'safe-dropdown';
const nameTip = overquotaSafeVariantC ? 'files_to_safe_no_del' : 'files_to_safe';

export const SafeDropdownButton: FC = memo(() => {
    const [shown, setShown] = useState(false);
    const dispatch = useDispatch();
    const ref = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const isStoredToSafe = useSelector(isSuccessfullyStoredToSafe);
    const storage = useSelector(getCurrentStorage);
    const wasShown = storeHelper.getValue(TOOLTIP_ID);
    const periodPassed = wasShown && storeHelper.isPassed(TOOLTIP_ID, { daysCount: 1 });
    const source = storage === EStorageType.trashbin ? 'trashbin' : 'cleaner';

    const showTip = (!wasShown || periodPassed) && isStoredToSafe && showTooltip;

    const handleDropdownClick = useCallback(
        (shown: boolean) => {
            sendDwhButton({ action: shown ? 'click' : 'close', name, source });
            setShown(shown);
        },
        [setShown, source]
    );

    useEffect(() => {
        setShowTooltip(!!isStoredToSafe);
    }, [isStoredToSafe]);

    useEffect(() => {
        sendDwhButton({ name, source });
    }, []);

    const handleClickRestore = useCallback(() => {
        sendDwhButton({ action: 'click', name: 'safe-dropdown-restore', source });
        setShown(false);
        dispatch(restoreFromSafeRequest());
    }, []);

    const handleClickRemove = useCallback(() => {
        sendDwhButton({ action: 'click', name: 'safe-dropdown-remove', source });
        setShown(false);
        dispatch(removeFromSafeAction());
    }, []);

    const handleTipShow = useCallback(() => {
        sendDwhTooltip({ name: nameTip, place: source });
    }, []);

    const handleTipClick = useCallback(() => {
        sendDwhTooltip({ action: 'click', name: nameTip, place: source });
        storeHelper.markAsShown(TOOLTIP_ID);
        setShowTooltip(false);
    }, []);

    const handleTipClose = useCallback(() => {
        sendDwhTooltip({ action: 'close', name: nameTip, place: source });
        storeHelper.markAsShown(TOOLTIP_ID);
        setShowTooltip(false);
    }, []);

    const handleClose = useCallback(() => {
        storeHelper.markAsShown(TOOLTIP_ID);
        setShowTooltip(false);
    }, []);

    return (
        <div className={styles.wrapper}>
            <Popover
                action="click"
                shown={shown}
                placement="bottom-end"
                onShownChange={handleDropdownClick}
                className={styles.popover}
                content={
                    <div className={styles.contentWrapper} data-test-id="safe-dropdown-content">
                        <Button
                            mode="tertiary"
                            before={<Icon20ArrowUturnLeftOutline />}
                            className={styles.contentButton}
                            data-test-id="restore-files"
                            onClick={handleClickRestore}
                        >
                            <span className={styles.text}>Вернуть файлы в Облако</span>
                        </Button>
                        <Button
                            mode="tertiary"
                            before={<Icon20DeleteOutline />}
                            className={styles.contentButton}
                            data-test-id="remove-files"
                            onClick={handleClickRemove}
                        >
                            <span className={styles.text}>Очистить сейф</span>
                        </Button>
                    </div>
                }
            >
                <Button
                    getRootRef={ref}
                    className={styles.dropdown}
                    mode="outline"
                    before={<SafeBoxIcon />}
                    after={
                        shown ? (
                            <Icon20ChevronUpOutline color="var(--vkui--color_icon_accent)" />
                        ) : (
                            <DropdownOutlineIcon color="var(--vkui--color_icon_accent)" />
                        )
                    }
                    data-test-id="safe-dropdown"
                >
                    <span className={styles.text}>Сейф</span>
                </Button>
            </Popover>
            {showTip && (
                <FloatingTooltip
                    target={ref}
                    className={styles.tip}
                    contentClassName={styles.tipContent}
                    placement={ETooltipPlacement.bottom}
                    size={ETooltipSize.large}
                    title={storage === EStorageType.trashbin ? 'Файлы из корзины — в сейфе' : 'Ваши файлы — в сейфе'}
                    text={
                        <div className={styles.tipText}>
                            Они не занимают место в Облаке.
                            <br />
                            Их можно вернуть, если будут нужны
                        </div>
                    }
                    qaId={TOOLTIP_ID}
                    buttonText="Хорошо"
                    buttonFitContent
                    closeOnOutsideClick={true}
                    onCrossClose={handleTipClose}
                    onClose={handleClose}
                    onClick={handleTipClick}
                    onShow={handleTipShow}
                />
            )}
        </div>
    );
});

SafeDropdownButton.displayName = 'SafeDropdownButton';
